import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-manrope font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">
      {title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </div>
);

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-slate-900">Terms of Service</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-manrope font-bold text-slate-900">Terms of Service</h1>
          </div>
          <p className="text-slate-500 text-sm">
            Last updated: July 2, 2025 &nbsp;·&nbsp; Effective date: July 2, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white rounded-xl border border-slate-200 p-8 md:p-12">

          <p className="text-slate-600 leading-relaxed mb-10">
            These Terms of Service ("<strong>Terms</strong>") govern your use of the online appointment booking platform operated by Vedamrita Ayurvedic Hospital ("<strong>Vedamrita</strong>", "<strong>we</strong>", "<strong>us</strong>") at{' '}
            <a href="https://booking.vedamrita.com" className="text-primary hover:underline">booking.vedamrita.com</a>{' '}
            (the "<strong>Service</strong>"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.
          </p>

          <Section title="1. Eligibility">
            <p>You must be at least 18 years of age to create an account and use the Service. By using the Service, you represent and warrant that you meet this requirement. Users below 18 may use the Service only under the supervision of a parent or legal guardian who accepts these Terms on their behalf.</p>
          </Section>

          <Section title="2. Account Registration">
            <ul className="list-disc pl-5 space-y-2">
              <li>You must provide accurate, current, and complete information when registering</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You are responsible for all activity that occurs under your account</li>
              <li>You must notify us immediately at <a href="mailto:info@vedamrita.com" className="text-primary hover:underline">info@vedamrita.com</a> if you suspect any unauthorised use of your account</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms</li>
            </ul>
          </Section>

          <Section title="3. Appointment Booking">
            <p><strong>Booking:</strong> Appointments booked through the Service are subject to availability and confirmation by Vedamrita. A booking confirmation does not constitute a guarantee of availability until confirmed by the hospital.</p>
            <p><strong>Accuracy of information:</strong> You are responsible for providing accurate details when booking — including the correct date, time, department, and patient information. Vedamrita is not liable for missed appointments resulting from incorrect information.</p>
            <p><strong>Consultation types:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>In-person:</strong> You must arrive at the hospital at least 10 minutes before your scheduled appointment</li>
              <li><strong>Video consultation:</strong> A Google Meet link will be provided via email. You are responsible for having a stable internet connection and a compatible device</li>
            </ul>
          </Section>

          <Section title="4. Cancellation and Rescheduling">
            <ul className="list-disc pl-5 space-y-2">
              <li>Appointments may be cancelled or rescheduled through your dashboard or by contacting us at least <strong>24 hours</strong> before the scheduled time</li>
              <li>Cancellations made less than 24 hours before the appointment may not be eligible for a refund</li>
              <li>In case of a no-show without prior cancellation, the appointment fee (if any) is non-refundable</li>
              <li>Vedamrita reserves the right to cancel or reschedule appointments due to unavailability of the doctor, medical emergencies, or circumstances beyond our control. In such cases, you will be notified and offered an alternative slot or a full refund</li>
            </ul>
          </Section>

          <Section title="5. Payments">
            <p>Payments for paid services are processed securely through <strong>Razorpay</strong>. By making a payment, you agree to Razorpay's terms and conditions. We do not store your card details.</p>
            <p className="mt-2">All prices are displayed in Indian Rupees (INR) and include applicable taxes unless stated otherwise. Prices are subject to change without notice.</p>
            <p className="mt-2"><strong>Refunds</strong> are processed within 5–7 business days of approval to the original payment method, subject to our cancellation policy above.</p>
          </Section>

          <Section title="6. Medical Disclaimer">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-semibold text-amber-800 mb-2">Important</p>
              <p className="text-amber-700 text-sm">
                The Service is a booking and scheduling platform only. Information provided on this platform does not constitute medical advice, diagnosis, or treatment. Always consult a qualified Ayurvedic physician or other healthcare professional for medical advice. In case of a medical emergency, contact emergency services immediately — do not rely on this platform for emergency care.
              </p>
            </div>
            <p className="mt-3">Vedamrita's doctors will provide consultations based on the information you supply. You are responsible for providing complete and accurate health information to ensure appropriate care.</p>
          </Section>

          <Section title="7. Google Calendar Integration">
            <p>If you choose to connect your Google Calendar, you authorise us to create calendar events for your appointments. You may revoke this access at any time from the Integrations settings page. We are not responsible for any issues arising from Google's services being unavailable.</p>
          </Section>

          <Section title="8. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Use the Service for any unlawful purpose or in violation of any applicable law</li>
              <li>Book appointments with no intention of attending (fraudulent bookings)</li>
              <li>Provide false or misleading information about your identity or health condition</li>
              <li>Attempt to gain unauthorised access to other users' accounts or our systems</li>
              <li>Use automated tools or bots to access or scrape the Service</li>
              <li>Harass, threaten, or abuse our staff, doctors, or other users</li>
              <li>Upload or transmit any malicious code or content</li>
            </ul>
          </Section>

          <Section title="9. Intellectual Property">
            <p>All content, branding, software, and materials on the Service — including the Vedamrita name, logo, and platform design — are the intellectual property of Vedamrita Ayurvedic Hospital and are protected under applicable Indian intellectual property law. You may not reproduce, distribute, or create derivative works without our prior written consent.</p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>To the maximum extent permitted by applicable law, Vedamrita shall not be liable for:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Any indirect, incidental, special, or consequential damages arising from your use of the Service</li>
              <li>Loss of data, appointments, or revenue</li>
              <li>Interruptions or errors in the Service due to circumstances beyond our control (server outages, third-party service failures, etc.)</li>
              <li>The outcome of medical consultations or treatments</li>
            </ul>
            <p className="mt-3">Our total liability to you for any claim arising from use of the Service shall not exceed the amount you paid us in the 3 months preceding the claim.</p>
          </Section>

          <Section title="11. Governing Law and Disputes">
            <p>
              These Terms are governed by the laws of India. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts in <strong>Hisar, Haryana, India</strong>. We encourage you to contact us first at{' '}
              <a href="mailto:info@vedamrita.com" className="text-primary hover:underline">info@vedamrita.com</a>{' '}
              to resolve any dispute before initiating legal proceedings.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on this page with a new effective date. Your continued use of the Service after such changes constitutes your acceptance of the revised Terms.</p>
          </Section>

          <Section title="13. Contact Us">
            <p>For any questions about these Terms, please contact us:</p>
            <div className="mt-3 bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
              <p><strong>Vedamrita Ayurvedic Hospital</strong></p>
              <p>Hisar - Tohana Rd, near MRF Agency, Barwala, Haryana 125121</p>
              <p>Email: <a href="mailto:info@vedamrita.com" className="text-primary hover:underline">info@vedamrita.com</a></p>
              <p>Phone: <a href="tel:+918570023400" className="text-primary hover:underline">+91 85700-23400</a></p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
